import React, { useEffect, useMemo, useState } from 'react';
import { io } from 'socket.io-client';
import {
  ArrowLeft, Award, Bell, Bot, Check, ChevronRight, Clock, Coins, Copy, DoorOpen, Gift,
  Gamepad2, Gauge, Globe2, Headphones, Home, KeyRound, LockKeyhole,
  LogOut, Menu, MessageCircle, Mic, Music, Plus, Radio, Search,
  HelpCircle, Settings, ShieldCheck, Sparkles, Star, Trophy, UserPlus, UserRound, Users,
  Trash2, Vibrate, Volume2, X, Zap
} from 'lucide-react';

const SESSION_KEY = 'koskim_session_id';
const AUTH_KEY = 'koskim_auth';
const MAX_AVATAR_ID = 13;

const CHARACTERS = [
  { key: 'zeynep', name: 'Kıvanç', role: 'Komutan', skill: 'Şok Tuzağı', color: '#12a463', icon: Zap },
  { key: 'mert', name: 'Ece', role: 'Analist', skill: 'İz Sensörü', color: '#f0a500', icon: Radio },
  { key: 'ada', name: 'Zeta', role: 'Mühendis', skill: 'Foton Duvarı', color: '#1689cf', icon: Gauge },
  { key: 'efe', name: 'Nova', role: 'Keşifçi', skill: 'Koruma Alanı', color: '#f07318', icon: ShieldCheck }
];

const PROFILE_AVATARS = Array.from({ length: MAX_AVATAR_ID }, (_, index) => ({
  id: index + 1,
  label: `Karakter ${index + 1}`,
  src: `/avatars/character_${index + 1}.png`
}));

const COSTUME_ART_ZOOM = {
  1: [100, 94, 94, 132], 2: [100, 99, 102, 111], 3: [100, 95, 181, 157],
  4: [100, 94, 94, 132], 5: [100, 94, 133, 119], 6: [100, 96, 143, 119],
  7: [100, 94, 135, 114], 8: [100, 94, 151, 102], 9: [100, 94, 107, 139],
  10: [100, 104, 101, 122], 11: [100, 99, 119, 151], 12: [100, 98, 107, 138],
  13: [116, 116, 116, 116]
};

const KARANTINACI_SKILL_FALLBACK = [
  { key: 'shock_trap', name: 'Şok Tuzağı', price: 1000, icon: '⚡', description: 'Arkanda tuzak bırakır; Karantinacıyı 3 saniye durdurur.' },
  { key: 'sensor_trap', name: 'İz Sensörü', price: 1000, icon: '◎', description: 'Tetiklenince Karantinacının yerini 3 saniye açığa çıkarır.' },
  { key: 'barrier', name: 'Foton Duvarı', price: 1000, icon: '▰', description: 'Kısa süreli geçilmez bir duvar oluşturur.' },
  { key: 'shield', name: 'Koruma Alanı', price: 1000, icon: '◯', description: 'Karantinacının içine giremediği güvenli alan oluşturur.' }
];

const EMPTY_PLATFORM = {
  games: { kostebek: [], karantinaci: [], son_sinyal: [] },
  activeRoom: null,
  onlineUsers: 0,
  karantinaciConfig: { difficulty: 'normal' }
};

function App() {
  const [socket] = useState(() => io({ autoConnect: true, reconnection: true, reconnectionDelay: 450 }));
  const [auth, setAuth] = useState(null);
  const [booting, setBooting] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authError, setAuthError] = useState('');
  const [platform, setPlatform] = useState(EMPTY_PLATFORM);
  const [view, setView] = useState('center');
  const [lobby, setLobby] = useState(null);
  const [sonSinyalLobby, setSonSinyalLobby] = useState(null);
  const [kostebekRoom, setKostebekRoom] = useState(null);
  const [kostebekMessages, setKostebekMessages] = useState([]);
  const [roomDialog, setRoomDialog] = useState(null);
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState({ incoming: [], outgoing: [] });
  const [toast, setToast] = useState('');
  const [invite, setInvite] = useState(null);
  const [socketOnline, setSocketOnline] = useState(socket.connected);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [view]);

  useEffect(() => {
    const showToast = message => {
      setToast(message);
      window.clearTimeout(showToast.timer);
      showToast.timer = window.setTimeout(() => setToast(''), 3200);
    };
    const requestPlatform = () => socket.emit('get_platform_state');
    const onConnect = () => {
      setSocketOnline(true);
      const sessionId = window.localStorage.getItem(SESSION_KEY);
      if (sessionId) socket.emit('auth_check', { sessionId });
      else setBooting(false);
    };
    const onDisconnect = () => setSocketOnline(false);
    const onAuthSuccess = payload => {
      window.localStorage.setItem(SESSION_KEY, payload.sessionId);
      window.localStorage.setItem(AUTH_KEY, 'true');
      setAuth(payload);
      setAuthOpen(false);
      setAuthError('');
      setBooting(false);
      requestPlatform();
      socket.emit('get_friends', { userId: payload.sessionId });
    };
    const onAccountProgress = payload => setAuth(current => current ? { ...current, ...payload } : payload);
    const onAuthFail = message => {
      setBooting(false);
      if (authOpen) setAuthError(message || 'Giriş yapılamadı.');
      else {
        window.localStorage.removeItem(SESSION_KEY);
        window.localStorage.removeItem(AUTH_KEY);
      }
    };
    const onPlatform = next => {
      const normalized = { ...EMPTY_PLATFORM, ...next, games: { ...EMPTY_PLATFORM.games, ...(next?.games || {}) } };
      setPlatform(normalized);
      if (next?.activeRoom?.gameType === 'karantinaci' && next.activeRoom.roomState) {
        setLobby(next.activeRoom.roomState);
        if (next.activeRoom.phase === 'waiting') setView('karantinaci-lobby');
      }
      if (next?.activeRoom?.gameType === 'kostebek' && next.activeRoom.roomState) {
        setKostebekRoom(next.activeRoom.roomState);
        setView(next.activeRoom.phase === 'waiting' ? 'kostebek-lobby' : 'kostebek-game');
      }
      if (next?.activeRoom?.gameType === 'son_sinyal' && next.activeRoom.roomState) {
        setSonSinyalLobby(next.activeRoom.roomState);
        if (next.activeRoom.phase === 'waiting') setView('son-sinyal-lobby');
      }
    };
    const onLobby = next => {
      setLobby(next);
      setView('karantinaci-lobby');
    };
    const onSonSinyalLobby = next => {
      setSonSinyalLobby(next);
      setView('son-sinyal-lobby');
    };
    const onKostebekState = next => {
      setKostebekRoom(next);
      setView(next?.status === 'waiting' ? 'kostebek-lobby' : 'kostebek-game');
    };
    const onKostebekJoined = payload => {
      if (payload?.roomState) onKostebekState(payload.roomState);
    };
    const onKostebekMessage = message => setKostebekMessages(current => [...current.slice(-29), message]);
    const onRoomExpired = ({ reason } = {}) => {
      setLobby(null);
      setKostebekRoom(null);
      setSonSinyalLobby(null);
      setKostebekMessages([]);
      setView('center');
      showToast(reason || 'Oda kapatıldı.');
      requestPlatform();
    };
    const onLaunch = ({ roomId }) => {
      if (roomId) window.location.assign(`/karantinaci/?roomId=${encodeURIComponent(roomId)}&online=1`);
    };
    const onSonSinyalLaunch = ({ roomId }) => {
      if (roomId) window.location.assign(`/son-sinyal/?roomId=${encodeURIComponent(roomId)}&online=1`);
    };
    const onFriends = next => setFriends(Array.isArray(next) ? next : []);
    const onFriendRequests = next => setFriendRequests({ incoming: next?.incoming || [], outgoing: next?.outgoing || [] });
    const onFriendRequestReceived = ({ fromName } = {}) => showToast(`${fromName || 'Bir oyuncu'} sana arkadaşlık isteği gönderdi.`);
    const onActionSuccess = message => showToast(message || 'İşlem tamamlandı.');
    const onActionError = message => showToast(message || 'İşlem yapılamadı.');
    const onReplaced = ({ reason } = {}) => showToast(reason || 'Oturum başka bir sekmede açıldı.');

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('auth_success', onAuthSuccess);
    socket.on('account_progress', onAccountProgress);
    socket.on('auth_fail', onAuthFail);
    socket.on('platform_state', onPlatform);
    socket.on('karantinaci_lobby_state', onLobby);
    socket.on('son_sinyal_lobby_state', onSonSinyalLobby);
    socket.on('room_state_update', onKostebekState);
    socket.on('joined_room_success', onKostebekJoined);
    socket.on('receive_message', onKostebekMessage);
    socket.on('platform_room_expired', onRoomExpired);
    socket.on('karantinaci_game_launch', onLaunch);
    socket.on('son_sinyal_game_launch', onSonSinyalLaunch);
    socket.on('friends_list_detailed', onFriends);
    socket.on('friend_requests_state', onFriendRequests);
    socket.on('friend_request_received', onFriendRequestReceived);
    socket.on('friend_action_success', onActionSuccess);
    socket.on('friend_action_error', onActionError);
    socket.on('platform_notice', onActionError);
    socket.on('karantinaci_session_replaced', onReplaced);
    socket.on('son_sinyal_session_replaced', onReplaced);
    socket.on('room_invite_received', setInvite);
    if (socket.connected) onConnect();
    return () => {
      window.clearTimeout(showToast.timer);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('auth_success', onAuthSuccess);
      socket.off('account_progress', onAccountProgress);
      socket.off('auth_fail', onAuthFail);
      socket.off('platform_state', onPlatform);
      socket.off('karantinaci_lobby_state', onLobby);
      socket.off('son_sinyal_lobby_state', onSonSinyalLobby);
      socket.off('room_state_update', onKostebekState);
      socket.off('joined_room_success', onKostebekJoined);
      socket.off('receive_message', onKostebekMessage);
      socket.off('platform_room_expired', onRoomExpired);
      socket.off('karantinaci_game_launch', onLaunch);
      socket.off('son_sinyal_game_launch', onSonSinyalLaunch);
      socket.off('friends_list_detailed', onFriends);
      socket.off('friend_requests_state', onFriendRequests);
      socket.off('friend_request_received', onFriendRequestReceived);
      socket.off('friend_action_success', onActionSuccess);
      socket.off('friend_action_error', onActionError);
      socket.off('platform_notice', onActionError);
      socket.off('karantinaci_session_replaced', onReplaced);
      socket.off('son_sinyal_session_replaced', onReplaced);
      socket.off('room_invite_received', setInvite);
    };
  }, [socket, authOpen]);

  const openAuth = mode => {
    setAuthMode(mode);
    setAuthError('');
    setAuthOpen(true);
  };
  const submitAuth = data => socket.emit(authMode === 'login' ? 'auth_login' : 'auth_register', data);
  const logout = () => {
    window.localStorage.removeItem(SESSION_KEY);
    window.localStorage.removeItem(AUTH_KEY);
    window.location.assign('/');
  };
  const createKostebekRoom = ({ name, isPrivate } = {}) => {
    socket.timeout(5000).emit('create_kostebek_room', { name, isPrivate }, (error, response) => {
      if (error || !response?.ok) return setToast(response?.reason || 'Köstebek Kim? odası oluşturulamadı.');
      setRoomDialog(null);
      setKostebekMessages([]);
      setKostebekRoom(response.roomState);
      setView('kostebek-lobby');
    });
  };
  const joinKostebekRoom = roomId => {
    socket.timeout(5000).emit('join_kostebek_room', { roomId }, (error, response) => {
      if (error || !response?.ok) return setToast(response?.reason || 'Köstebek Kim? odasına katılınamadı.');
      setKostebekRoom(response.roomState);
      setView(response.roomState?.status === 'waiting' ? 'kostebek-lobby' : 'kostebek-game');
    });
  };
  const createKarantinaciLobby = ({ name, isPrivate } = {}) => {
    socket.timeout(5000).emit('create_karantinaci_lobby', { name, isPrivate }, (error, response) => {
      if (error || !response?.ok) return setToast(response?.reason || 'Oda oluşturulamadı.');
      setRoomDialog(null);
      setLobby(response.roomState);
      setView('karantinaci-lobby');
    });
  };
  const joinKarantinaciLobby = roomId => {
    socket.timeout(5000).emit('join_karantinaci_lobby', { roomId }, (error, response) => {
      if (error || !response?.ok) return setToast(response?.reason || 'Odaya katılınamadı.');
      setLobby(response.roomState);
      setView('karantinaci-lobby');
    });
  };
  const createSonSinyalLobby = ({ name, isPrivate } = {}) => {
    socket.timeout(5000).emit('create_son_sinyal_lobby', { name, isPrivate }, (error, response) => {
      if (error || !response?.ok) return setToast(response?.reason || 'Son Sinyal odası oluşturulamadı.');
      setRoomDialog(null);
      setSonSinyalLobby(response.roomState);
      setView('son-sinyal-lobby');
    });
  };
  const joinSonSinyalLobby = roomId => {
    socket.timeout(5000).emit('join_son_sinyal_lobby', { roomId }, (error, response) => {
      if (error || !response?.ok) return setToast(response?.reason || 'Son Sinyal odasına katılınamadı.');
      setSonSinyalLobby(response.roomState);
      if (response.roomState?.phase === 'waiting') setView('son-sinyal-lobby');
      else window.location.assign(`/son-sinyal/?roomId=${encodeURIComponent(roomId)}&online=1`);
    });
  };
  const joinByCode = code => {
    socket.timeout(5000).emit('join_platform_room_by_code', { code }, (error, response) => {
      if (error || !response?.ok) return setToast(response?.reason || 'Oda kodu bulunamadı.');
      setRoomDialog(null);
      if (response.gameType === 'kostebek') {
        setKostebekRoom(response.roomState);
        setView(response.roomState?.status === 'waiting' ? 'kostebek-lobby' : 'kostebek-game');
      } else if (response.gameType === 'karantinaci') {
        setLobby(response.roomState);
        setView('karantinaci-lobby');
      } else {
        setSonSinyalLobby(response.roomState);
        setView('son-sinyal-lobby');
      }
    });
  };
  const quickPlay = gameType => {
    const active = platform.activeRoom;
    if (active) {
      if (active.gameType === 'kostebek') setView(active.phase === 'waiting' ? 'kostebek-lobby' : 'kostebek-game');
      else if (active.gameType === 'karantinaci') {
        if (active.phase === 'waiting') setView('karantinaci-lobby');
        else window.location.assign(`/karantinaci/?roomId=${encodeURIComponent(active.roomId)}&online=1`);
      } else if (active.phase === 'waiting') setView('son-sinyal-lobby');
      else window.location.assign(`/son-sinyal/?roomId=${encodeURIComponent(active.roomId)}&online=1`);
      return;
    }
    const rooms = platform.games[gameType] || [];
    const available = rooms.find(room => (room.playerCount || 0) < (room.maxPlayers || 4));
    if (available) {
      if (gameType === 'kostebek') joinKostebekRoom(available.id);
      else if (gameType === 'karantinaci') joinKarantinaciLobby(available.id);
      else joinSonSinyalLobby(available.id);
      return;
    }
    if (gameType === 'kostebek') createKostebekRoom({ name: `${auth.username} Odası`, isPrivate: false });
    else if (gameType === 'karantinaci') createKarantinaciLobby({ name: `${auth.username} Ekibi`, isPrivate: false });
    else createSonSinyalLobby({ name: `${auth.username} Frekansı`, isPrivate: false });
  };
  const leaveActiveRoom = () => socket.timeout(5000).emit('leave_active_room', {}, (_error, response) => {
    if (!response?.ok) return setToast(response?.reason || 'Odadan çıkılamadı.');
    setLobby(null);
    setKostebekRoom(null);
    setSonSinyalLobby(null);
    setKostebekMessages([]);
    setView('center');
    socket.emit('get_platform_state');
  });
  const deleteActiveRoom = () => socket.timeout(5000).emit('delete_active_room', {}, (_error, response) => {
    if (!response?.ok) return setToast(response?.reason || 'Oda silinemedi.');
    setLobby(null);
    setKostebekRoom(null);
    setSonSinyalLobby(null);
    setKostebekMessages([]);
    setView('center');
    socket.emit('get_platform_state');
  });
  const updateProfileAvatar = (avatarId, costumeTier = 0) => socket.timeout(5000).emit('profile_update', { avatarId, costumeTier }, (_error, response) => {
    if (!response?.ok) setToast(response?.reason || 'Karakter güncellenemedi.');
  });
  const purchaseProfileCharacter = avatarId => socket.timeout(5000).emit('purchase_character', { avatarId }, (error, response) => {
    if (error || !response?.ok) return setToast(response?.reason || 'Karakter satın alınamadı.');
    if (response.auth) setAuth(response.auth);
    setToast(response.alreadyOwned ? 'Bu karakter zaten koleksiyonunda.' : 'Karakter koleksiyonuna eklendi.');
  });
  const purchaseProfileCostume = (avatarId, tier) => socket.timeout(5000).emit('purchase_costume_tier', { avatarId, tier }, (error, response) => {
    if (error || !response?.ok) return setToast(response?.reason || 'Kostüm seviyesi açılamadı.');
    if (response.auth) setAuth(response.auth);
    setToast(response.alreadyOwned ? 'Bu kostüm zaten koleksiyonunda.' : `Seviye ${tier} kostümü açıldı.`);
  });
  const claimDaily = () => socket.timeout(5000).emit('claim_daily_gift', {}, (error, response) => {
    if (error || !response?.ok) return setToast(response?.reason || 'Günlük hediye alınamadı.');
    if (response.auth) setAuth(response.auth);
    setToast(`${response.cycleDay}. gün hediyen kasana eklendi.`);
    setView('pass');
  });

  if (booting) return <Splash />;
  if (!auth) {
    return <Landing onAuth={openAuth}>{authOpen && <AuthModal mode={authMode} error={authError} onMode={setAuthMode} onClose={() => setAuthOpen(false)} onSubmit={submitAuth} />}</Landing>;
  }

  const karantinaciLobbyMode = view === 'karantinaci-lobby' && lobby;
  const sonSinyalLobbyMode = view === 'son-sinyal-lobby' && sonSinyalLobby;
  const kostebekMode = (view === 'kostebek-lobby' || view === 'kostebek-game') && kostebekRoom;
  const immersiveMode = karantinaciLobbyMode || sonSinyalLobbyMode || kostebekMode;
  return (
    <div className={`app-shell ${immersiveMode ? 'lobby-mode' : ''}`}>
      {!immersiveMode && <Sidebar auth={auth} view={view} onView={setView} onLogout={logout} notificationCount={friendRequests.incoming.length} />}
      <div className="app-column">
        {!immersiveMode && <Topbar auth={auth} online={socketOnline} onView={setView} notificationCount={friendRequests.incoming.length} />}
        <main className="app-main">
          {view === 'center' && <GameCenter auth={auth} platform={platform} onQuick={quickPlay} onOpenRoom={setRoomDialog} onJoinKostebek={joinKostebekRoom} onJoinKarantinaci={joinKarantinaciLobby} onJoinSonSinyal={joinSonSinyalLobby} onView={setView} onClaimDaily={claimDaily} />}
          {karantinaciLobbyMode && <KarantinaciLobby auth={auth} lobby={lobby} friends={friends} difficulty={platform.karantinaciConfig?.difficulty || 'normal'} socket={socket} onExit={leaveActiveRoom} onDelete={deleteActiveRoom} />}
          {sonSinyalLobbyMode && <SonSinyalLobby auth={auth} lobby={sonSinyalLobby} friends={friends} socket={socket} onExit={leaveActiveRoom} onDelete={deleteActiveRoom} />}
          {kostebekMode && <KostebekRoom auth={auth} room={kostebekRoom} messages={kostebekMessages} friends={friends} socket={socket} onExit={leaveActiveRoom} onDelete={deleteActiveRoom} />}
          {view === 'friends' && <FriendsView auth={auth} friends={friends} requests={friendRequests} socket={socket} />}
          {view === 'notifications' && <NotificationsView requests={friendRequests} socket={socket} />}
          {view === 'profile' && <ProfileView auth={auth} platform={platform} onView={setView} onAvatar={updateProfileAvatar} onPurchaseCharacter={purchaseProfileCharacter} onPurchaseCostume={purchaseProfileCostume} />}
          {view === 'pass' && <DlinkPassView auth={auth} socket={socket} />}
          {view === 'settings' && <SettingsView />}
          {view === 'help' && <HelpView />}
        </main>
      </div>
      {!immersiveMode && <MobileNav view={view} onView={setView} notificationCount={friendRequests.incoming.length} />}
      {invite && <InviteBanner invite={invite} onAccept={() => {
        const next = invite;
        setInvite(null);
        if (next?.gameType === 'karantinaci') joinKarantinaciLobby(next.roomId);
        else if (next?.gameType === 'son_sinyal') joinSonSinyalLobby(next.roomId);
        else joinKostebekRoom(next.roomId);
      }} onClose={() => setInvite(null)} />}
      {roomDialog && <RoomDialog config={roomDialog} auth={auth} onClose={() => setRoomDialog(null)} onCreate={data => roomDialog.gameType === 'kostebek' ? createKostebekRoom(data) : roomDialog.gameType === 'karantinaci' ? createKarantinaciLobby(data) : createSonSinyalLobby(data)} onJoinCode={joinByCode} />}
      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  );
}

function Brand({ compact = false }) {
  return <div className={`brand ${compact ? 'compact' : ''}`}><span>Dlink</span><b>Games</b></div>;
}

function ProfileFace({ avatarId = 1, costumeTier = 0, className = '' }) {
  const normalized = Math.min(MAX_AVATAR_ID, Math.max(1, Number(avatarId) || 1));
  const tier = normalized === 13 ? 0 : Math.max(0, Math.min(3, Number(costumeTier) || 0));
  const source = tier > 0 ? `/avatars/costumes/character_${normalized}_tier_${tier}.png` : `/avatars/character_${normalized}.png`;
  const zoom = COSTUME_ART_ZOOM[normalized]?.[tier] || 100;
  return <span className={`profile-face ${className}`} data-avatar-id={normalized} data-costume-tier={tier} style={{ backgroundImage: `url('${source}')`, '--costume-art-zoom': `${zoom}%` }} aria-hidden="true" />;
}

function profileStats(auth) {
  const xp = Math.max(0, Math.floor(Number(auth?.accountXp ?? auth?.passXp) || 0));
  const spendableXp = Math.max(0, Math.floor(Number(auth?.spendableXp ?? auth?.xp) || 0));
  const coins = Math.max(0, Math.floor(Number(auth?.coins) || 0));
  const earnedXp = xp;
  const level = Math.max(1, Math.floor(Number(auth?.level) || Math.floor(earnedXp / 1000) + 1));
  return { xp, spendableXp, coins, earnedXp, level, progress: Math.min(100, (earnedXp % 1000) / 10) };
}

const NAV_ITEMS = [
  { id: 'center', label: 'Oyun Merkezi', icon: Gamepad2 },
  { id: 'friends', label: 'Arkadaşlar', icon: Users },
  { id: 'notifications', label: 'Bildirimler', icon: Bell },
  { id: 'profile', label: 'Profil', icon: Trophy },
  { id: 'pass', label: 'Dlink Pass', icon: Gift },
  { id: 'settings', label: 'Ayarlar', icon: Settings },
  { id: 'help', label: 'Yardım', icon: HelpCircle }
];

function Sidebar({ auth, view, onView, onLogout, notificationCount = 0 }) {
  const [open, setOpen] = useState(false);
  return <>
    <button className="mobile-menu-button" onClick={() => setOpen(true)} aria-label="Menüyü aç"><Menu /></button>
    <aside className={`sidebar ${open ? 'open' : ''}`}>
      <div className="sidebar-head"><Brand /><button className="sidebar-close" onClick={() => setOpen(false)}><X /></button></div>
      <nav>{NAV_ITEMS.map(item => {
        const Icon = item.icon;
        return <button key={item.id} className={view === item.id ? 'active' : ''} onClick={() => { onView(item.id); setOpen(false); }}><Icon /><span>{item.label}</span>{item.id === 'notifications' && notificationCount > 0 && <i>{notificationCount}</i>}</button>;
      })}</nav>
      <div className="sidebar-foot">
        <section className="pass-card"><span>🎁</span><b>Dlink Pass</b><p>Aşama {auth?.dlinkPass?.currentStage || 1} · Kutuları aç, rozetleri topla.</p><button onClick={() => { onView('pass'); setOpen(false); }}>Pass'i Aç</button></section>
        <button className="logout-button" onClick={onLogout}><LogOut /> Çıkış Yap</button>
      </div>
    </aside>
    {open && <button className="sidebar-scrim" onClick={() => setOpen(false)} aria-label="Menüyü kapat" />}
  </>;
}

function Topbar({ auth, online, onView, notificationCount = 0 }) {
  const stats = profileStats(auth);
  return <header className="topbar">
    <h1>Dlink Games</h1>
    <div className="topbar-actions">
      <button className="profile-progress" onClick={() => onView('profile')}><ProfileFace avatarId={auth.avatarId} costumeTier={auth.costumeTier} /><span><b>{auth.username}</b><small><i>{stats.level}</i><em><u style={{ width: `${stats.progress}%` }} /></em>{stats.xp.toLocaleString('tr-TR')} XP</small></span></button>
      <button className="square-button" aria-label="Arkadaşlar" onClick={() => onView('friends')}><Users /></button>
      <button className="square-button notification-button" aria-label="Bildirimler" onClick={() => onView('notifications')}><Bell />{notificationCount > 0 && <i>{notificationCount}</i>}</button>
      <span className={`server-dot ${online ? 'online' : ''}`} title={online ? 'Bağlı' : 'Bağlanıyor'} />
    </div>
  </header>;
}

function MobileNav({ view, onView, notificationCount = 0 }) {
  const items = NAV_ITEMS.slice(0, 4);
  return <nav className="mobile-bottom-nav" aria-label="Mobil ana menü">{items.map(item => { const Icon = item.icon; return <button key={item.id} className={view === item.id ? 'active' : ''} onClick={() => onView(item.id)}><Icon /><span>{item.label}</span>{item.id === 'notifications' && notificationCount > 0 && <i>{notificationCount}</i>}</button>; })}</nav>;
}

function GameCenter({ auth, platform, onQuick, onOpenRoom, onJoinKostebek, onJoinKarantinaci, onJoinSonSinyal, onView, onClaimDaily }) {
  const rooms = [
    ...(platform.games.son_sinyal || []).map(room => ({ ...room, game: 'Son Sinyal', tone: 'cyan' })),
    ...(platform.games.karantinaci || []).map(room => ({ ...room, game: 'Karantinacı', tone: 'green' })),
    ...(platform.games.kostebek || []).map(room => ({ ...room, game: 'Köstebek Kim?', tone: 'orange' }))
  ];
  const active = platform.activeRoom;
  return <div className="page game-center">
    <section className="play-heading"><h2><Star /> Oyun zamanı <Sparkles /></h2><p>Hoş geldin {auth.username}; odanı seç, takımını kur.</p></section>
    <section className={`daily-gift-banner ${auth?.progression?.dailyGift?.available ? 'available' : ''}`}><Gift /><span><b>{auth?.progression?.dailyGift?.available ? `Gün ${auth.progression.dailyGift.cycleDay} hediyen hazır` : 'Bugünkü hediyeni aldın'}</b><small>Her gün gel; XP kazan, çok düşük ihtimalle yeni karakter çıkar.</small></span><button disabled={!auth?.progression?.dailyGift?.available} onClick={onClaimDaily}>{auth?.progression?.dailyGift?.available ? 'Hediyeyi Al' : 'Yarın görüşürüz'}</button></section>
    {active && <section className="active-room-banner"><Radio /><span><b>Aktif odan devam ediyor</b><small>{active.gameName} · {active.roomName || active.code || active.roomId}</small></span><button onClick={() => active.gameType === 'karantinaci' ? (active.phase === 'waiting' ? onView('karantinaci-lobby') : window.location.assign(`/karantinaci/?roomId=${encodeURIComponent(active.roomId)}&online=1`)) : active.gameType === 'son_sinyal' ? (active.phase === 'waiting' ? onView('son-sinyal-lobby') : window.location.assign(`/son-sinyal/?roomId=${encodeURIComponent(active.roomId)}&online=1`)) : onView(active.phase === 'waiting' ? 'kostebek-lobby' : 'kostebek-game')}>Odaya Dön</button></section>}

    <section className="game-showcase">
      <GameShowcaseCard type="mole" title={<>Köstebek<br />Kim?</>} onQuick={() => onQuick('kostebek')} onCreate={() => onOpenRoom({ gameType: 'kostebek', mode: 'create', isPrivate: false })} onFriends={() => onOpenRoom({ gameType: 'kostebek', mode: 'create', isPrivate: true })} />
      <GameShowcaseCard type="quarantine" title="Karantinacı" onQuick={() => onQuick('karantinaci')} onCreate={() => onOpenRoom({ gameType: 'karantinaci', mode: 'create', isPrivate: false })} onFriends={() => onOpenRoom({ gameType: 'karantinaci', mode: 'create', isPrivate: true })} />
      <GameShowcaseCard type="signal" title="Son Sinyal" onQuick={() => onQuick('son_sinyal')} onCreate={() => onOpenRoom({ gameType: 'son_sinyal', mode: 'create', isPrivate: false })} onFriends={() => onOpenRoom({ gameType: 'son_sinyal', mode: 'create', isPrivate: true })} />
    </section>

    <section className="rooms-section">
      <div className="section-heading"><h2><DoorOpen /> Açık Odalar</h2><button onClick={() => onOpenRoom({ mode: 'join' })}>Oda Koduyla Katıl <KeyRound /></button></div>
      <div className="room-table">
        {rooms.length ? rooms.slice(0, 7).map(room => <RoomRow key={`${room.game}-${room.id}`} room={room} onJoin={() => room.game === 'Karantinacı' ? onJoinKarantinaci(room.id) : room.game === 'Son Sinyal' ? onJoinSonSinyal(room.id) : onJoinKostebek(room.id)} />) : <button className="empty-room" onClick={() => onOpenRoom({ gameType: 'son_sinyal', mode: 'create', isPrivate: false })}><Plus /><span><b>İlk açık odayı sen kur</b><small>Arkadaşlarını veya botları ekleyebilirsin.</small></span></button>}
      </div>
    </section>
  </div>;
}

function GameShowcaseCard({ type, title, onQuick, onCreate, onFriends }) {
  return <article className={`showcase-card ${type}`}>
    <header className="showcase-title"><h2>{title}</h2></header>
    <div className="showcase-art"><img src={type === 'mole' ? '/art/kostebek-card.jpg' : type === 'quarantine' ? '/art/karantinaci-card.jpg' : '/art/son-sinyal-card-v2.jpg'} alt="" /></div>
    <div className="showcase-actions"><button className="quick" onClick={onQuick}>Hızlı Oyna</button><button onClick={onCreate}>Oda Kur</button><button onClick={onFriends}>Arkadaşla Oyna</button></div>
  </article>;
}

function RoomRow({ room, onJoin }) {
  const players = Number(room.playerCount ?? room.players?.length ?? 0);
  const max = Number(room.maxPlayers || 4);
  const waiting = room.status === 'waiting' || room.phase === 'waiting' || !room.phase;
  return <article className="room-row">
    <span className={`room-game-icon ${room.tone}`}>{room.game === 'Karantinacı' ? '☣' : room.game === 'Son Sinyal' ? '◉' : '⛏'}</span>
    <div className="room-title"><b>{room.game}</b><small>{room.name || room.roomName || (room.tone === 'green' ? 'Yeşil Takım Odası' : 'Hızlı ve Eğlenceli')}</small></div>
    <b className="room-capacity">{players}/{max}</b><span className={`room-signal ${room.tone}`}><i /><i /><i /><i /></span>
    <span className="room-privacy">{room.isPrivate ? <><LockKeyhole /> Özel</> : <><Globe2 /> Herkese Açık</>}</span>
    <button className="row-join" onClick={onJoin} disabled={players >= max || !waiting}>Katıl</button>
  </article>;
}

function RoomDialog({ config, auth, onClose, onCreate, onJoinCode }) {
  const joining = config.mode === 'join';
  const [name, setName] = useState(`${auth.username} ${config.gameType === 'kostebek' ? 'Odası' : config.gameType === 'son_sinyal' ? 'Frekansı' : 'Ekibi'}`);
  const [code, setCode] = useState('');
  const [isPrivate, setIsPrivate] = useState(Boolean(config.isPrivate));
  const gameName = config.gameType === 'kostebek' ? 'Köstebek Kim?' : config.gameType === 'son_sinyal' ? 'Son Sinyal' : 'Karantinacı';
  return <div className="modal-backdrop"><section className="room-dialog">
    <button className="modal-close" onClick={onClose} aria-label="Kapat"><X /></button><Brand compact />
    <h2>{joining ? 'Odaya katıl' : `${gameName} odası kur`}</h2>
    <p>{joining ? 'Arkadaşının paylaştığı altı haneli oda kodunu yaz.' : 'Odanı adlandır; açık tut veya yalnızca kodu bilen arkadaşlarına aç.'}</p>
    {joining ? <form onSubmit={event => { event.preventDefault(); onJoinCode(code); }}><label>Oda kodu<input className="code-input" autoFocus value={code} onChange={event => setCode(event.target.value.replace(/[^a-z0-9]/gi, '').toUpperCase().slice(0, 6))} placeholder="Q7K2MX" required /></label><button className="auth-submit">Odaya Katıl</button></form> : <form onSubmit={event => { event.preventDefault(); onCreate({ name: name.trim(), isPrivate }); }}><label>Oda adı<input autoFocus value={name} onChange={event => setName(event.target.value)} maxLength={28} required /></label><button type="button" className={`privacy-option ${isPrivate ? 'selected' : ''}`} onClick={() => setIsPrivate(value => !value)}>{isPrivate ? <LockKeyhole /> : <Globe2 />}<span><b>{isPrivate ? 'Özel oda' : 'Herkese açık oda'}</b><small>{isPrivate ? 'Yalnızca oda kodunu bilenler katılır.' : 'Oda, oyun merkezindeki açık odalarda görünür.'}</small></span><i className={`toggle ${isPrivate ? 'on' : ''}`} /></button><button className="auth-submit">Odayı Kur</button></form>}
    {joining && <button className="room-dialog-switch" onClick={onClose}>Vazgeç</button>}
  </section></div>;
}

function roomCharacterIndex(member, fallback = 0) {
  const avatar = Number(member?.avatarId);
  if (avatar >= 1 && avatar <= MAX_AVATAR_ID) return (avatar - 1) % CHARACTERS.length;
  const keyIndex = CHARACTERS.findIndex(character => character.key === member?.characterKey);
  return keyIndex >= 0 ? keyIndex : fallback % CHARACTERS.length;
}

function roomAvatarId(member, fallback = 0) {
  const avatar = Number(member?.avatarId);
  return avatar >= 1 && avatar <= MAX_AVATAR_ID ? avatar : (fallback % 12) + 1;
}

function RoomIdentityCard({ member, slot, isHost, canRemove, onRemove, roleLabel = 'Oyuncu', roleColor = '#526176' }) {
  const characterIndex = roomCharacterIndex(member, slot);
  const avatarId = roomAvatarId(member, slot);
  return <article className={`lobby-player-card ${member ? '' : 'empty'}`}>
    {isHost && <span className="host-crown">♛</span>}<b className="slot-label">{isHost ? 'HOST' : member?.isBot ? 'BOT' : member ? 'DAVETLİ' : 'BOŞ'}</b>
    <ProfileFace avatarId={avatarId} costumeTier={member?.costumeTier} className="lobby-avatar" />
    <h2 title={member?.name}>{member?.name || 'Oyuncu Bekleniyor'}</h2><p style={{ color: roleColor }}>{member ? roleLabel : CHARACTERS[characterIndex].role}</p>
    {member && <span className={`ready-pill ${member.isReady ? 'on' : ''}`}>{member.isReady ? 'Hazır' : 'Hazır Değil'}</span>}
    {member?.isSelf && <small className="you-label">SEN</small>}{canRemove && <button className="remove-lobby-bot" onClick={event => { event.stopPropagation(); onRemove(); }} aria-label="Botu kaldır"><X /></button>}
  </article>;
}

function KostebekRoom({ auth, room, messages, friends, socket, onExit, onDelete }) {
  const [message, setMessage] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(room.timeLeft || 0);
  const [selectedVoteId, setSelectedVoteId] = useState(null);
  const [votePending, setVotePending] = useState(false);
  const [voteError, setVoteError] = useState('');
  const [matchBox, setMatchBox] = useState(null);
  const [claimedMatchBoxId, setClaimedMatchBoxId] = useState(null);
  const players = room.players || [];
  const me = players.find(player => player.isSelf);
  const isHost = Boolean(me?.isHost);
  const waiting = room.status === 'waiting';
  const playing = room.status === 'playing';
  const voting = room.status === 'voting';
  const results = room.status === 'results';
  const slots = Array.from({ length: room.maxPlayers || 4 }, (_, index) => players[index] || null);
  useEffect(() => { setTimeLeft(room.timeLeft || 0); }, [room.timeLeft, room.status]);
  useEffect(() => {
    if (room.status !== 'voting' || me?.votedForId) setSelectedVoteId(null);
    setVoteError('');
    setVotePending(false);
  }, [room.status, me?.votedForId]);
  useEffect(() => {
    const tick = ({ timeLeft: next }) => setTimeLeft(next);
    socket.on('game_tick', tick);
    return () => socket.off('game_tick', tick);
  }, [socket]);
  const emit = (event, payload = {}) => socket.emit(event, { roomId: room.id, ...payload });
  const copyCode = () => navigator.clipboard?.writeText(room.code || room.id);
  const sendMessage = event => {
    event.preventDefault();
    const text = message.trim();
    if (!text || !me) return;
    socket.timeout(5000).emit('send_message', { roomId: room.id, message: { id: `msg-${Date.now()}`, senderId: me.id, text } }, (_error, response) => {
      if (response?.accepted) setMessage('');
    });
  };
  const submitVote = () => {
    if (!selectedVoteId || !me || votePending || me.votedForId) return;
    setVotePending(true);
    setVoteError('');
    socket.timeout(5000).emit('vote_player', { roomId: room.id, voterId: me.id, targetId: selectedVoteId }, (error, response) => {
      setVotePending(false);
      if (error || !response?.ok) setVoteError(response?.reason || 'Oy sunucuya ulaşmadı. Tekrar dene.');
    });
  };
  const phaseTitle = waiting ? 'Köstebek Kim? Lobisi' : playing ? 'Kelime Turunda' : voting ? 'Köstebeği Seç' : 'Tur Sonucu';
  const mole = players.find(player => player.id === room.result?.moleId || player.role === 'mole');
  const resultWinner = room.result?.winningSide === 'players' ? 'OYUNCULAR KAZANDI' : 'KÖSTEBEK KAZANDI';
  const myBoxAward = room.result?.boxAwards?.find(award => award.playerId === me?.id);
  const openMatchBox = () => socket.timeout(5000).emit('open_reward_box', { boxId: myBoxAward?.boxId }, (_error, response) => {
    if (response?.ok) setMatchBox(response.box);
  });
  const collectMatchBox = () => socket.timeout(5000).emit('ack_reward_box', { boxId: matchBox?.id }, () => {
    setClaimedMatchBoxId(matchBox?.id);
    setMatchBox(null);
  });
  return <div className="lobby-screen kostebek-room"><section className="lobby-panel">
    <header className="lobby-top"><button className="lobby-back" onClick={isHost && waiting ? onDelete : onExit} aria-label={isHost && waiting ? 'Odayı sil' : 'Odadan çık'}>{isHost && waiting ? <Trash2 /> : <ArrowLeft />}</button><div><h1>{phaseTitle}</h1><p>{waiting ? 'Tek profil, tek karakter; ekibini tamamla ve herkes hazır olduğunda başla.' : playing ? 'Kelimeyi anlat; köstebek olduğunu belli etme.' : voting ? 'Şüphelendiğin oyuncuyu seç.' : 'Oylar sayıldı. Yeni tur için lobiyi sıfırla.'}</p></div><button className="room-code copyable" onClick={copyCode}><small>Oda Kodu</small><b>{room.code || room.id}</b><Copy /></button><span className="privacy-chip">{room.isPrivate ? <LockKeyhole /> : <Globe2 />}{room.isPrivate ? 'Özel' : 'Açık'}</span></header>

    {(playing || voting || results) && <section className={`secret-word ${me?.role === 'mole' ? 'mole' : ''}`}><span>{me?.role === 'mole' ? 'KÖSTEBEK' : 'OYUNCU'}</span><b>{me?.word || 'Kelimen hazırlanıyor'}</b><small><Clock /> {Math.max(0, timeLeft)} sn</small></section>}

    {waiting && <div className="lobby-player-grid">{slots.map((member, slot) => <RoomIdentityCard key={member?.id || slot} member={member} slot={slot} isHost={Boolean(member?.isHost)} canRemove={isHost && member?.isBot} onRemove={() => emit('bot_remove', { botId: member.id })} />)}</div>}

    {playing && <section className="kostebek-play-layout"><div className="kostebek-roster">{players.map((player, index) => <article key={player.id}><ProfileFace avatarId={player.avatarId || index + 1} costumeTier={player.costumeTier} /><span><b>{player.name}</b><small>{player.isBot ? 'Bot oyuncu' : player.isSelf ? 'Sen' : 'Oyuncu'}{player.connected === false ? ' · Bağlantı koptu' : ''}</small></span>{player.isHost && <em>HOST</em>}</article>)}</div><p className="round-hint">Sırayla konuşun, kelimenizi doğrudan söylemeden ipucu verin. Hazır olduğunuzda host oylamayı açar.</p></section>}

    {voting && <section className="vote-panel"><header><div><span>GİZLİ OYLAMA</span><h2>Sence köstebek kim?</h2></div><strong>{room.votedCount || 0}/{room.eligibleVoterCount || players.length} oy</strong></header>{me?.votedForId ? <div className="vote-locked"><Check /><div><b>Oyun kaydedildi</b><p>Diğer oyuncuların seçimi bekleniyor. {Math.max(0, timeLeft)} saniye kaldı.</p></div></div> : <><div className="vote-candidate-grid">{players.filter(player => !player.isSelf && player.connected !== false).map((player, index) => <button key={player.id} type="button" className={selectedVoteId === player.id ? 'selected' : ''} onClick={() => { setSelectedVoteId(player.id); setVoteError(''); }}><ProfileFace avatarId={player.avatarId || index + 1} /><span><b>{player.name}</b><small>{player.isBot ? 'BOT' : 'OYUNCU'}</small></span>{selectedVoteId === player.id && <Check />}</button>)}</div>{voteError && <p className="vote-error">{voteError}</p>}<button className="confirm-vote" disabled={!selectedVoteId || votePending} onClick={submitVote}>{votePending ? 'OYUN GÖNDERİLİYOR…' : 'SEÇİMİMİ YAPTIM'}</button></>}</section>}

    {waiting && <div className="lobby-command-row"><button onClick={() => emit('bot_add')} disabled={!isHost || players.length >= 4}><Bot /> Bot Ekle</button><button onClick={copyCode}><Copy /> Kodu Kopyala</button><button onClick={() => setInviteOpen(value => !value)} disabled={players.length >= (room.maxPlayers || 4)}><UserPlus /> Davet Et</button><button className={`ready-command ${me?.isReady ? 'selected' : ''}`} onClick={() => emit('player_ready_toggle', { sessionId: auth.sessionId })}><Check /> {me?.isReady ? 'Hazırsın' : 'Hazırım'}</button></div>}
    {waiting && inviteOpen && <OnlineFriendInvite friends={friends} onInvite={friend => socket.emit('invite_friend_to_room', { userId: auth.sessionId, friendId: friend.id, roomId: room.id })} />}
    {waiting && (isHost ? <button className="start-game" disabled={!room.canStart} onClick={() => emit('game_start')}><span>▷</span> Oyunu Başlat</button> : <div className="waiting-host"><Radio /> Oda sahibi oyunu başlatacak</div>)}
    {playing && isHost && <button className="start-game vote-start" onClick={() => emit('trigger_voting')}><Users /> Oylamaya Geç</button>}
    {results && <section className={`result-summary ${room.result?.moleCaught ? 'players-win' : 'mole-win'}`}><header><span>OYUN SONUCU</span><h2>{resultWinner}</h2></header>{mole && <div className="mole-reveal"><ProfileFace avatarId={mole.avatarId} className="result-avatar" /><div><small>GERÇEK KÖSTEBEK</small><b>{mole.name}</b><p>{mole.word}</p></div></div>}<h3>Kim kime oy verdi?</h3><div className="vote-results">{players.map(player => { const target = players.find(candidate => candidate.id === player.votedForId); return <article key={player.id}><ProfileFace avatarId={player.avatarId} className="result-avatar" /><div><b>{player.name}</b><small>{target ? `${target.name} için oy verdi` : 'Oy kullanmadı'}</small></div><strong>{player.votesReceived || 0} oy</strong></article>; })}</div>{isHost ? <button onClick={() => emit('reset_game')}>Yeni Tur İçin Lobiye Dön</button> : <p className="result-wait">Host yeni turu hazırlıyor.</p>}</section>}

    {results && myBoxAward?.boxId && claimedMatchBoxId !== myBoxAward.boxId && <section className="match-box-earned"><Gift /><div><b>Maç Kutusu Kazandın!</b><small>+{myBoxAward.passPoints || 0} Pass puanı · Kutuyu şimdi çatlat.</small></div><button onClick={openMatchBox}>KUTUYU AÇ</button></section>}
    {matchBox && <BoxOpeningModal box={matchBox} onCollect={collectMatchBox} />}

    {!results && <section className="lobby-chat full-chat"><h3><MessageCircle /> {playing ? 'Kelime Sohbeti' : voting ? 'Oylama Sohbeti' : 'Oda Sohbeti'}</h3><div className="chat-messages">{messages.map(item => <p key={item.id}><b>{item.senderName}:</b> {item.text}</p>)}{!messages.length && <p className="chat-empty">Oda sohbeti hazır.</p>}</div><form onSubmit={sendMessage}><input value={message} onChange={event => setMessage(event.target.value)} maxLength={50} placeholder="Mesaj yaz..." /><button aria-label="Gönder">›</button></form></section>}
  </section></div>;
}

function KarantinaciLobby({ auth, lobby, friends, difficulty, socket, onExit, onDelete }) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [skillShopOpen, setSkillShopOpen] = useState(false);
  const [skillBusy, setSkillBusy] = useState('');
  const [skillError, setSkillError] = useState('');
  const [message, setMessage] = useState('');
  const members = useMemo(() => [...(lobby.players || []), ...(lobby.bots || [])].sort((a, b) => a.slot - b.slot), [lobby]);
  const slots = Array.from({ length: lobby.maxPlayers || 4 }, (_, slot) => members.find(member => member.slot === slot) || null);
  const me = (lobby.players || []).find(player => player.isSelf);
  const isHost = Boolean(me?.isHost);
  const skillCatalog = KARANTINACI_SKILL_FALLBACK.map(fallback => ({
    ...fallback,
    ...(auth.karantinaciSkillCatalog || []).find(skill => skill.key === fallback.key)
  }));
  const selectedSkillKeys = me?.selectedSkills || (me?.selectedSkill ? [me.selectedSkill] : []);
  const selectedSkills = skillCatalog.filter(skill => selectedSkillKeys.includes(skill.key));
  const skillCapacity = Math.max(1, Number(auth?.progression?.skillCapacity) || 1);
  const emit = (event, payload = {}) => socket.emit(event, { roomId: lobby.id, ...payload });
  const leave = () => isHost ? onDelete() : onExit();
  const sendMessage = event => {
    event.preventDefault();
    const text = message.trim();
    if (!text) return;
    socket.timeout(5000).emit('karantinaci_lobby_message', { roomId: lobby.id, text }, (_error, response) => {
      if (response?.ok) setMessage('');
    });
  };
  const difficultyLabel = difficulty === 'hard' ? 'Zor' : difficulty === 'easy' ? 'Kolay' : 'Normal';
  const changeSkill = (skillKey, purchase = false) => {
    if (skillBusy) return;
    const nextSkillKeys = purchase
      ? selectedSkillKeys
      : skillKey == null
        ? []
        : selectedSkillKeys.includes(skillKey)
          ? selectedSkillKeys.filter(key => key !== skillKey)
          : [...selectedSkillKeys, skillKey];
    if (!purchase && nextSkillKeys.length > skillCapacity) {
      setSkillError(`Bu kostüm seviyesi en fazla ${skillCapacity} yetenek taşıyabilir.`);
      return;
    }
    setSkillBusy(skillKey || 'none');
    setSkillError('');
    socket.timeout(5000).emit(purchase ? 'karantinaci_skill_purchase' : 'karantinaci_skill_equip', purchase ? { roomId: lobby.id, skillKey } : { roomId: lobby.id, skillKeys: nextSkillKeys }, (error, response) => {
      setSkillBusy('');
      if (error || !response?.ok) {
        setSkillError(response?.reason || 'Yetenek işlemi tamamlanamadı.');
        return;
      }
      if (response.roomState?.players?.some(player => player.isSelf)) {
        setSkillError('');
      } else {
        setSkillError('Yetenek kaydedildi fakat lobi seçimi doğrulanamadı. Tekrar dene.');
      }
    });
  };

  return <div className="lobby-screen">
    <section className="lobby-panel">
      <header className="lobby-top"><button className="lobby-back" onClick={leave} aria-label={isHost ? 'Odayı sil' : 'Odadan çık'}>{isHost ? <Trash2 /> : <ArrowLeft />}</button><div><h1>Karantinacı Lobisi</h1><p>Ekibini tamamla, hazır ol ve birlikte kaç.</p></div><button className="room-code copyable" onClick={() => navigator.clipboard?.writeText(lobby.code || lobby.id)}><small>Oda Kodu</small><b>{lobby.code || lobby.id}</b><Copy /></button><span className="privacy-chip">{lobby.isPrivate ? <LockKeyhole /> : <Globe2 />}{lobby.isPrivate ? 'Özel' : 'Açık'}</span></header>

      <div className="lobby-player-grid">{slots.map((member, slot) => <LobbyPlayerCard key={member?.id || slot} member={member} slot={slot} isMe={Boolean(member?.isSelf)} isHost={Boolean(member?.isHost)} canRemove={isHost && member?.isBot} onRemove={() => emit('karantinaci_lobby_remove_bot', { botId: member.id })} />)}</div>

      <section className="skill-loadout-bar"><div><span>YETENEK DONANIMI · {selectedSkills.length}/{skillCapacity} YUVA</span><b>{selectedSkills.length ? selectedSkills.map(skill => `${skill.icon} ${skill.name}`).join(' · ') : 'Yetenek seçilmedi'}</b><small>Her yetenek tur başına 2 hak · {Number(auth.spendableXp ?? auth.xp ?? 0).toLocaleString('tr-TR')} harcanabilir XP</small></div><button onClick={() => setSkillShopOpen(true)}><Zap /> Yetenekleri Yönet</button></section>

      <div className="lobby-command-row">
        <button onClick={() => emit('karantinaci_lobby_add_bot')} disabled={!isHost || members.length >= 4}><Bot /> Bot Ekle</button>
        <button onClick={() => navigator.clipboard?.writeText(lobby.code || lobby.id)}><Copy /> Kodu Kopyala</button>
        <button onClick={() => setInviteOpen(value => !value)} disabled={members.length >= (lobby.maxPlayers || 4)}><UserPlus /> Davet Et</button>
        <button className={`ready-command ${me?.isReady ? 'selected' : ''}`} onClick={() => emit('karantinaci_lobby_ready')}><Check /> {me?.isReady ? 'Hazırsın' : 'Hazırım'}</button>
      </div>

      {inviteOpen && <OnlineFriendInvite friends={friends} onInvite={friend => socket.emit('invite_friend_to_karantinaci', { userId: auth.sessionId, friendId: friend.id, roomId: lobby.id })} />}

      {isHost ? <button className="start-game" disabled={!lobby.canStart} onClick={() => emit('karantinaci_lobby_start')}><span>▷</span> Oyunu Başlat</button> : <div className="waiting-host"><Radio /> Host oyunu başlatacak</div>}

      <div className="lobby-bottom-grid">
        <section className="lobby-chat"><h3><MessageCircle /> Sohbet</h3><div className="chat-messages">{(lobby.messages || []).map(item => <p key={item.id}><b>{item.senderName || 'Dlink'}:</b> {item.text}</p>)}{!(lobby.messages || []).length && <p className="chat-empty">Takım sohbeti hazır.</p>}</div><form onSubmit={sendMessage}><input value={message} onChange={event => setMessage(event.target.value)} maxLength={80} placeholder="Mesaj yaz..." /><button aria-label="Gönder">›</button></form></section>
        <section className="lobby-info"><div className="voice-summary"><span><i /> Sesli Sohbet <small>Bağlı</small></span><div>{slots.map((member, slot) => member && <ProfileFace avatarId={roomAvatarId(member, slot)} costumeTier={member.costumeTier} className="mini-avatar" key={member.id} />)}<button aria-label="Mikrofon"><Mic /></button></div></div><label>Zorluk<div><Gauge /><b>{difficultyLabel}</b><ChevronRight /></div></label></section>
      </div>
    </section>
    {skillShopOpen && <SkillShop auth={auth} catalog={skillCatalog} selectedSkillKeys={selectedSkillKeys} capacity={skillCapacity} busy={skillBusy} error={skillError} onClose={() => { setSkillShopOpen(false); setSkillError(''); }} onEquip={key => changeSkill(key, false)} onPurchase={key => changeSkill(key, true)} />}
  </div>;
}

const SIGNAL_ROLES = [
  { name: 'Yankı Uzmanı', skill: 'Yankı Yemi', color: '#7b6cff' },
  { name: 'Işık Teknisyeni', skill: 'Işık Darbesi', color: '#ff9f43' },
  { name: 'Akım Koşucusu', skill: 'Enerji Atılımı', color: '#16a9cc' },
  { name: 'Sessizlik Gözcüsü', skill: 'Sessiz Alan', color: '#17a96b' }
];

function SonSinyalLobby({ auth, lobby, friends, socket, onExit, onDelete }) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [message, setMessage] = useState('');
  const members = [...(lobby.players || []), ...(lobby.bots || [])];
  const slots = Array.from({ length: lobby.maxPlayers || 4 }, (_, index) => members[index] || null);
  const me = (lobby.players || []).find(player => player.isSelf);
  const isHost = Boolean(me?.isHost);
  const emit = (event, payload = {}) => socket.emit(event, { roomId: lobby.id, ...payload });
  const sendMessage = event => {
    event.preventDefault();
    const text = message.trim();
    if (!text) return;
    socket.timeout(5000).emit('son_sinyal_lobby_message', { roomId: lobby.id, text }, (_error, response) => {
      if (response?.ok) setMessage('');
    });
  };
  const startGame = () => socket.timeout(5000).emit('son_sinyal_lobby_start', { roomId: lobby.id }, () => {});
  return <div className="lobby-screen signal-lobby-screen"><section className="lobby-panel signal-lobby-panel">
    <header className="lobby-top"><button className="lobby-back" onClick={isHost ? onDelete : onExit} aria-label={isHost ? 'Odayı sil' : 'Odadan çık'}>{isHost ? <Trash2 /> : <ArrowLeft />}</button><div><span className="signal-kicker"><Radio /> ACİL FREKANS</span><h1>Son Sinyal Lobisi</h1><p>Üç röleyi birlikte çalıştır, gölgeden kaç ve merkez antenden tahliye ol.</p></div><button className="room-code copyable" onClick={() => navigator.clipboard?.writeText(lobby.code || lobby.id)}><small>Oda Kodu</small><b>{lobby.code || lobby.id}</b><Copy /></button><span className="privacy-chip">{lobby.isPrivate ? <LockKeyhole /> : <Globe2 />}{lobby.isPrivate ? 'Özel' : 'Açık'}</span></header>

    <div className="lobby-player-grid">{slots.map((member, slot) => {
      const role = SIGNAL_ROLES[((Number(member?.avatarId) || slot + 1) - 1) % SIGNAL_ROLES.length];
      return <RoomIdentityCard key={member?.id || slot} member={member} slot={slot} isHost={Boolean(member?.isHost)} roleLabel={member ? `${role.skill} · ${role.name}` : 'Frekans bekleniyor'} roleColor={role.color} canRemove={isHost && member?.isBot} onRemove={() => emit('son_sinyal_lobby_remove_bot', { botId: member.id })} />;
    })}</div>

    <section className="signal-mission-strip"><article><Radio /><b>3 Sinyal Rölesi</b><small>Enerji hücresini taşı</small></article><article><Users /><b>Eş Zamanlı Aktivasyon</b><small>Rölede iki kişi buluş</small></article><article><Zap /><b>2 Yetenek Hakkı</b><small>Profil karakterine özel</small></article><article><Clock /><b>{lobby.mission?.roundSeconds || 150} saniye</b><small>Son sinyali kaçırma</small></article></section>

    <div className="lobby-command-row">
      <button onClick={() => emit('son_sinyal_lobby_add_bot')} disabled={!isHost || members.length >= 4}><Bot /> Bot Ekle</button>
      <button onClick={() => navigator.clipboard?.writeText(lobby.code || lobby.id)}><Copy /> Kodu Kopyala</button>
      <button onClick={() => setInviteOpen(value => !value)} disabled={members.length >= 4}><UserPlus /> Davet Et</button>
      <button className={`ready-command ${me?.isReady ? 'selected' : ''}`} onClick={() => emit('son_sinyal_lobby_ready')}><Check /> {me?.isReady ? 'Hazırsın' : 'Hazırım'}</button>
    </div>
    {inviteOpen && <OnlineFriendInvite friends={friends} onInvite={friend => socket.emit('invite_friend_to_son_sinyal', { userId: auth.sessionId, friendId: friend.id, roomId: lobby.id })} />}
    {isHost ? <button className="start-game signal-start" disabled={!lobby.canStart} onClick={startGame}><Radio /> Görevi Başlat</button> : <div className="waiting-host"><Radio /> Oda sahibi görevi başlatacak</div>}

    <div className="lobby-bottom-grid"><section className="lobby-chat"><h3><MessageCircle /> Ekip Kanalı</h3><div className="chat-messages">{(lobby.messages || []).map(item => <p key={item.id}><b>{item.senderName || 'Dlink'}:</b> {item.text}</p>)}</div><form onSubmit={sendMessage}><input value={message} onChange={event => setMessage(event.target.value)} maxLength={80} placeholder="Mesaj yaz..." /><button aria-label="Gönder">›</button></form></section><section className="lobby-info signal-brief"><span className="eyebrow">GÖREV BRİFİNGİ</span><h3>Sinyal kesilmeden çıkış yap</h3><p>Enerji hücresini alan oyuncuyu koru. Röle etkinleşirken iki ekip üyesi alan içinde kalmalı. Yakalanan ekip arkadaşının yanında durarak onu ayağa kaldır.</p><div><ShieldCheck /> Sunucu kontrollü fizik · Bot devralma · Güvenli yeniden bağlanma</div></section></div>
  </section></div>;
}

function SkillShop({ auth, catalog, selectedSkillKeys = [], capacity = 1, busy, error, onClose, onEquip, onPurchase }) {
  const owned = new Set(auth.ownedKarantinaciSkills || []);
  const selected = new Set(selectedSkillKeys);
  return <div className="modal-backdrop skill-shop-backdrop"><section className="skill-shop">
    <button className="modal-close" onClick={onClose} aria-label="Kapat"><X /></button>
    <span className="eyebrow">KARANTİNACI DONANIMI</span><h2>Yeteneklerini hazırla</h2><p>Seçili kostümün {capacity} yetenek yuvası taşıyor. Satın aldığın yetenek profilinde kalır.</p>
    <div className="skill-shop-balance"><Trophy /><span><small>HARCANABİLİR XP · {selected.size}/{capacity} YUVA</small><b>{Number(auth.spendableXp ?? auth.xp ?? 0).toLocaleString('tr-TR')} XP</b></span></div>
    <div className="skill-shop-grid">{catalog.map(skill => {
      const isOwned = owned.has(skill.key);
      const isSelected = selected.has(skill.key);
      const cannotAfford = Number(auth.spendableXp ?? auth.xp ?? 0) < skill.price;
      return <article key={skill.key} className={isSelected ? 'selected' : ''}><i>{skill.icon}</i><div><h3>{skill.name}</h3><p>{skill.description}</p><small>{isOwned ? 'PROFİLİNDE' : `${Number(skill.price).toLocaleString('tr-TR')} XP`}</small></div>{isOwned ? <button disabled={Boolean(busy) || (!isSelected && selected.size >= capacity)} onClick={() => onEquip(skill.key)}>{busy === skill.key ? 'Kaydediliyor…' : isSelected ? 'Çıkar' : 'Kullan'}</button> : <button disabled={Boolean(busy) || cannotAfford} onClick={() => onPurchase(skill.key)}>{cannotAfford ? 'XP Yetersiz' : busy === skill.key ? 'Alınıyor…' : 'Satın Al'}</button>}</article>;
    })}</div>
    <button className={`no-skill-option ${selected.size ? '' : 'selected'}`} disabled={Boolean(busy) || !selected.size} onClick={() => onEquip(null)}><X /> Tüm yetenekleri çıkar</button>
    {error && <p className="skill-shop-error">{error}</p>}
  </section></div>;
}

function OnlineFriendInvite({ friends = [], onInvite }) {
  const onlineFriends = friends.filter(friend => friend.isOnline);
  return <section className="invite-drawer"><header><div><b>Çevrimiçi arkadaşlar</b><small>Oyundaki boş slota davet gönder.</small></div><span>{onlineFriends.length} çevrimiçi</span></header><div>{onlineFriends.length ? onlineFriends.map(friend => <button key={friend.id} onClick={() => onInvite(friend)}><ProfileFace avatarId={friend.avatarId} costumeTier={friend.costumeTier} /><span><b>{friend.name}</b><small>Şimdi çevrimiçi</small></span><UserPlus /></button>) : <p>Şu anda davet edilebilecek çevrimiçi arkadaşın yok.</p>}</div></section>;
}

function LobbyPlayerCard({ member, slot, isMe, isHost, canRemove, onRemove }) {
  const skills = KARANTINACI_SKILL_FALLBACK.filter(item => (member?.selectedSkills || (member?.selectedSkill ? [member.selectedSkill] : [])).includes(item.key));
  const label = isHost ? 'HOST' : member?.isBot ? 'BOT' : member ? 'DAVETLİ' : 'BOŞ';
  return <article className={`lobby-player-card ${member ? '' : 'empty'}`}>
    {isHost && <span className="host-crown">♛</span>}<b className="slot-label">{label}</b>
    <ProfileFace avatarId={roomAvatarId(member, slot)} costumeTier={member?.costumeTier} className="lobby-avatar" />
    <h2>{member?.name || 'Oyuncu Bekleniyor'}</h2><p style={{ color: skills.length ? '#12a463' : '#7a8190' }}>{member ? (skills.length ? skills.map(skill => skill.icon).join(' ') + ` ${skills.length} yetenek` : 'Yeteneksiz') : 'Slot hazır'}</p>
    <span className={`ready-pill ${member?.isReady ? 'on' : ''}`}>{member?.isReady ? 'Hazır' : 'Hazır Değil'}</span>
    {isMe && <small className="you-label">SEN</small>}{canRemove && <button className="remove-lobby-bot" onClick={onRemove} aria-label="Botu kaldır"><X /></button>}
  </article>;
}

function FriendsView({ auth, friends, requests = { incoming: [], outgoing: [] }, socket }) {
  const [friendName, setFriendName] = useState('');
  const addFriend = event => { event.preventDefault(); if (!friendName.trim()) return; socket.emit('add_friend_by_name', { userId: auth.sessionId, friendName: friendName.trim() }); setFriendName(''); };
  const respond = (requestId, action) => socket.emit('respond_friend_request', { requestId, action });
  return <div className="page simple-page"><span className="eyebrow">SOSYAL</span><h1>Arkadaşlar</h1><p>Bir kullanıcıyı eklemek için önce onayını alırsın; yalnızca kabul edilen arkadaşlar oyun daveti alabilir.</p><form className="friend-search" onSubmit={addFriend}><Search /><input value={friendName} onChange={event => setFriendName(event.target.value)} placeholder="Kullanıcı adıyla arkadaş ara" /><button>İstek Gönder</button></form>
    {requests.incoming.length > 0 && <section className="friend-request-section"><h2>Gelen istekler</h2>{requests.incoming.map(request => <article key={request.id}><ProfileFace avatarId={request.avatarId} costumeTier={request.costumeTier} /><span><b>{request.name}</b><small>Seni arkadaş eklemek istiyor</small></span><div><button className="accept" onClick={() => respond(request.id, 'accept')}><Check /> Kabul Et</button><button onClick={() => respond(request.id, 'reject')}><X /> Reddet</button></div></article>)}</section>}
    {requests.outgoing.length > 0 && <section className="outgoing-request-list"><h2>Gönderilen istekler</h2>{requests.outgoing.map(request => <article key={request.id}><ProfileFace avatarId={request.avatarId} costumeTier={request.costumeTier} /><span><b>{request.name}</b><small>Onay bekleniyor</small></span><button onClick={() => socket.emit('cancel_friend_request', { requestId: request.id })}>İptal</button></article>)}</section>}
    <div className="friend-grid">{friends.map(friend => <article key={friend.id}><ProfileFace avatarId={friend.avatarId} costumeTier={friend.costumeTier} /><div><b>{friend.name}</b><small className={friend.isOnline ? 'online-text' : ''}>{friend.isOnline ? 'Çevrimiçi' : 'Çevrimdışı'}</small></div><div className="friend-card-actions"><button title="Arkadaşlıktan çıkar" onClick={() => socket.emit('remove_friend', { userId: auth.sessionId, friendId: friend.id })}><X /></button><button title="Kullanıcıyı engelle" onClick={() => socket.emit('block_user', { targetId: friend.id })}><ShieldCheck /></button></div></article>)}{friends.length === 0 && <div className="empty-panel"><Users /><h3>Ekibin henüz boş</h3><p>Bir kullanıcı adı yazarak arkadaşlık isteği gönder.</p></div>}</div></div>;
}

function NotificationsView({ requests = { incoming: [], outgoing: [] }, socket }) {
  return <div className="page simple-page"><span className="eyebrow">BİLDİRİMLER</span><h1>Bildirim merkezi</h1><p>Arkadaşlık istekleri ve oyun haberleri burada görünür.</p><div className="notice-list">{requests.incoming.map(request => <article className="actionable-notice" key={request.id}><ProfileFace avatarId={request.avatarId} costumeTier={request.costumeTier} /><div><b>{request.name} arkadaşlık isteği gönderdi</b><p>Onaylamadan arkadaş listene eklenmez.</p><span><button onClick={() => socket.emit('respond_friend_request', { requestId: request.id, action: 'accept' })}>Kabul Et</button><button onClick={() => socket.emit('respond_friend_request', { requestId: request.id, action: 'reject' })}>Reddet</button></span></div></article>)}{requests.incoming.length === 0 && <article><span>✓</span><div><b>Bekleyen isteğin yok</b><p>Yeni bildirim geldiğinde burada göreceksin.</p></div></article>}<article><span>🎁</span><div><b>Günlük hediyeni unutma</b><p>Oyun merkezindeki hediyeyi her gün bir kez alabilirsin.</p></div></article></div></div>;
}

function ProfileView({ auth, platform, onView, onAvatar, onPurchaseCharacter, onPurchaseCostume }) {
  const stats = profileStats(auth);
  const characters = auth?.progression?.characters || PROFILE_AVATARS.map(avatar => ({ ...avatar, name: avatar.label, owned: true, price: 1500, ownedCostumeTier: 0, passive: { name: 'Hazırlanıyor', description: 'Karakter pasifi hazırlanıyor.' } }));
  const [previewId, setPreviewId] = useState(Number(auth.avatarId));
  const [previewTier, setPreviewTier] = useState(Number(auth.costumeTier || 0));
  const selected = characters.find(character => character.id === previewId) || characters[0];
  const ownedTier = Number(selected?.ownedCostumeTier ?? selected?.unlockedCostumeTier ?? 0);
  const tierInfo = selected?.costumeTiers?.find(item => item.tier === previewTier);
  const visibleCostumeTiers = selected?.costumeTiers?.length ? selected.costumeTiers : [{ tier: 0 }];
  const selectCharacter = character => {
    setPreviewId(character.id);
    setPreviewTier(character.id === Number(auth.avatarId) ? Number(auth.costumeTier || 0) : 0);
  };
  const primaryAction = () => {
    if (!selected.owned) return onPurchaseCharacter(selected.id);
    if (previewTier > ownedTier) return onPurchaseCostume(selected.id, previewTier);
    return onAvatar(selected.id, previewTier);
  };
  const actionDisabled = selected?.owned && previewTier > ownedTier + 1;
  const actionLabel = !selected?.owned
    ? `${Number(selected?.price || 0).toLocaleString('tr-TR')} XP ile Aç`
    : previewTier > ownedTier
      ? actionDisabled ? `Önce Seviye ${ownedTier + 1}` : `5.000 XP ile Seviye ${previewTier}'i Aç`
      : Number(auth.avatarId) === selected?.id && Number(auth.costumeTier || 0) === previewTier ? 'Kullanımda' : 'Bu Görünümü Kullan';
  return <div className="page simple-page profile-page">
    <button className="mobile-profile-back" onClick={() => onView('center')}><ArrowLeft /> Profil</button>
    <section className="profile-hero"><ProfileFace avatarId={auth.avatarId} costumeTier={auth.costumeTier} /><div><h2>{auth.username}</h2><span><i>{stats.level}</i><em><u style={{ width: `${stats.progress}%` }} /></em>{stats.xp.toLocaleString('tr-TR')} Hesap XP</span><small>{stats.spendableXp.toLocaleString('tr-TR')} harcanabilir XP · {stats.coins.toLocaleString('tr-TR')} Dlink Coin</small></div></section>
    <section className="profile-character-section character-collection"><span className="eyebrow">KAHRAMAN KOLEKSİYONU</span><h2>Karakterini incele, aç ve geliştir</h2><p className="collection-intro">Karakterlerin çoğu kutudan çok düşük ihtimalle çıkabilir veya XP ile doğrudan açılabilir. Premium KNVC yalnızca 2.000 XP ile açılır. Toplam hesap XP'n harcama yaptığında azalmaz.</p><div className="profile-character-grid">{characters.map(character =>
      <button key={character.id} className={`${previewId === character.id ? 'selected' : ''} ${character.owned ? '' : 'locked'} ${character.rarity || ''}`} onClick={() => selectCharacter(character)}><ProfileFace avatarId={character.id} costumeTier={0} className="account-avatar" /><b>{character.name || `Karakter ${character.id}`}</b><small>{character.owned ? `${character.passive?.name || 'Pasif hazır'}` : `${Number(character.price || 0).toLocaleString('tr-TR')} XP`}</small>{Number(auth.avatarId) === character.id && <Check />}{!character.owned && <LockKeyhole />}</button>
    )}</div>
      {selected && <div className="character-detail"><ProfileFace avatarId={selected.id} costumeTier={previewTier} className="character-detail-avatar" /><div><span className="eyebrow">{selected.rarity || 'starter'} · {selected.owned ? 'KOLEKSİYONUNDA' : 'KİLİTLİ'}</span><h3>{selected.name}</h3><b>{selected.passive?.name}</b><p>{selected.passive?.description}</p><small>{tierInfo?.description || `${previewTier === 0 ? 1 : previewTier === 1 ? 1 : previewTier === 2 ? 2 : 4} yetenek yuvası`}</small><button className="collection-primary-action" disabled={actionDisabled || (Number(auth.avatarId) === selected.id && Number(auth.costumeTier || 0) === previewTier)} onClick={primaryAction}>{actionLabel}</button></div><div className="costume-tier-picker">{visibleCostumeTiers.map(({ tier }) => {
        const owned = tier <= ownedTier;
        return <button key={tier} className={`${previewTier === tier ? 'selected' : ''} ${owned ? '' : 'locked'}`} onClick={() => setPreviewTier(tier)}><ProfileFace avatarId={selected.id} costumeTier={tier} /><span>{tier === 0 ? 'Klasik' : `Seviye ${tier}`}</span><small>{tier === 0 ? '1 yuva' : `${tier === 1 ? 1 : tier === 2 ? 2 : 4} yuva · ${owned ? 'Açık' : '5.000 XP'}`}</small>{!owned && <LockKeyhole />}</button>;
      })}</div></div>}
    </section>
    <div className="stat-grid"><article><Trophy /><b>{stats.spendableXp.toLocaleString('tr-TR')}</b><span>Harcanabilir XP</span></article><article><Gift /><b>{auth?.progression?.boxes?.length || 0}</b><span>Bekleyen Kutu</span></article><article><Users /><b>{platform.onlineUsers || 1}</b><span>Çevrimiçi</span></article></div>
  </div>;
}

function DlinkPassView({ auth, socket }) {
  const pass = auth?.dlinkPass || {
    seasonName: 'Kurucu Sezonu', points: 0, currentStage: 1, maxStage: 30, stepXp: 100,
    nextStageXp: 100, stageProgressPercent: 0, stages: [], badges: []
  };
  const [claimingStage, setClaimingStage] = useState(null);
  const [openingBox, setOpeningBox] = useState(null);
  const [error, setError] = useState('');
  const nextReward = pass.stages?.find(stage => stage.claimable);
  const boxes = auth?.progression?.boxes || [];
  const atFinalStage = Number(pass.currentStage) >= Number(pass.maxStage);
  const remainingXp = Math.max(0, Number(pass.nextStageXp || 0) - Number(pass.points ?? pass.xp ?? 0));

  const claim = stage => {
    if (claimingStage) return;
    setError('');
    setClaimingStage(stage);
    socket.timeout(5000).emit('claim_dlink_pass_reward', { stage }, (timeoutError, response) => {
      setClaimingStage(null);
      if (timeoutError || !response?.ok) {
        setError(response?.reason || 'Kutu şu anda açılamadı. Bağlantını kontrol edip tekrar dene.');
        return;
      }
      if (response.reward?.box) beginOpenBox(response.reward.box);
    });
  };

  const beginOpenBox = box => {
    setError('');
    socket.timeout(5000).emit('open_reward_box', { boxId: box.id }, (timeoutError, response) => {
      if (timeoutError || !response?.ok) {
        setError(response?.reason || 'Kutu hazırlanamadı. Bağlantını kontrol edip tekrar dene.');
        return;
      }
      setOpeningBox(response.box);
    });
  };

  const collectBox = boxId => socket.timeout(5000).emit('ack_reward_box', { boxId }, () => setOpeningBox(null));

  return <div className="page dlink-pass-page">
    <section className="dlink-pass-hero">
      <div className="pass-hero-copy"><span className="eyebrow">{pass.seasonName}</span><h1>Ödül Rotası</h1><p>Oynadığın her gerçek maç rotanı ilerletir. Her durakta kesin XP, bazı duraklarda ayrıca kutu veya koleksiyon rozeti bulunur.</p></div>
      <div className="pass-level-orb"><small>AŞAMA</small><b>{pass.currentStage}</b><span>/ {pass.maxStage}</span></div>
      <div className="pass-progress-block"><div><b>{Number(pass.points ?? pass.xp ?? 0).toLocaleString('tr-TR')} rota puanı</b><span>{atFinalStage ? 'Kurucu rotası tamamlandı' : `Sonraki ödüle ${remainingXp.toLocaleString('tr-TR')} puan`}</span></div><em><i style={{ width: `${pass.stageProgressPercent || 0}%` }} /></em><small>{Number(auth?.accountXp || 0).toLocaleString('tr-TR')} toplam XP · {Number(auth?.spendableXp ?? auth?.xp ?? 0).toLocaleString('tr-TR')} harcanabilir XP</small></div>
    </section>

    {boxes.length > 0 && <section className="unopened-boxes"><header><div><span className="eyebrow">KUTU KASASI</span><h2>{boxes.length} kutun açılmayı bekliyor</h2></div><small>Kutuyu çatlatmak için 3–5 kez dokun</small></header><div>{boxes.map(box => <button key={box.id} className={`reward-box-card ${box.type}`} onClick={() => beginOpenBox(box)}><span className="reward-box-visual"><Gift /></span><b>{box.name}</b><small>{box.requiredTaps} dokunuş · {box.source}</small><em>AÇ</em></button>)}</div></section>}
    {nextReward && <section className="pass-ready-banner">{nextReward.hasBox ? <Gift /> : nextReward.hasBadge ? <Award /> : <Star />}<span><b>{nextReward.hasBox ? 'Yeni kutun ve XP ödülün hazır' : nextReward.hasBadge ? `${nextReward.badge?.name || 'Yeni rozet'} ve XP hazır` : `${nextReward.xpReward || 100} XP hazır`}</b><small>{nextReward.stage}. rota ödülünü hesabına ekle.</small></span><button onClick={() => claim(nextReward.stage)} disabled={claimingStage === nextReward.stage}>{claimingStage === nextReward.stage ? 'Alınıyor…' : 'Ödülü Al'}</button></section>}
    {error && <p className="pass-error" role="alert">{error}</p>}

    <section className="pass-road-section"><header><div><span className="eyebrow">KURUCU ÖDÜL ROTASI</span><h2>Sıradaki kesin ödülünü gör</h2></div><small>Sunucu her durağı bir kez teslim eder</small></header><div className="pass-road">
      {(pass.stages || []).map(stage => <article key={stage.stage} className={`${stage.unlocked ? 'unlocked' : 'locked'} ${stage.claimable ? 'claimable' : ''} ${stage.claimed ? 'claimed' : ''}`}>
        <span className="stage-number">{stage.stage}</span>
        <div className="stage-reward">{stage.hasBox ? <><Gift /><b>{stage.claimed ? 'Alındı' : stage.claimable ? 'Hazır!' : stage.boxType === 'season' ? 'Sezon Kasası' : stage.boxType === 'super' ? 'Süper Kutu' : 'Dlink Kutusu'}</b></> : stage.hasBadge ? <><Award /><b>{stage.claimed ? 'Alındı' : stage.badge?.name || 'Rozet'}</b></> : <><Star /><b>{stage.xpReward || 100} XP</b></>}</div>
        <small>{stage.thresholdXp.toLocaleString('tr-TR')} puan</small>
        {stage.claimable && <button onClick={() => claim(stage.stage)} disabled={claimingStage === stage.stage}>{claimingStage === stage.stage ? '…' : 'Al'}</button>}
        {stage.claimed && <Check className="stage-check" />}
        {!stage.unlocked && <LockKeyhole className="stage-lock" />}
      </article>)}
    </div></section>

    {openingBox && <BoxOpeningModal box={openingBox} onCollect={() => collectBox(openingBox.id)} />}
  </div>;
}

function rewardText(reward) {
  if (reward.type === 'account_xp' || reward.type === 'xp') return `+${reward.amount} XP`;
  if (reward.type === 'coins') return `+${reward.amount} Dlink Coin`;
  if (reward.type === 'character') return `${reward.name || `Karakter ${reward.characterId}`} açıldı!`;
  if (reward.type === 'character_power') return `${reward.name || `Karakter ${reward.characterId}`} +${reward.amount} güç`;
  if (reward.type === 'skill') return `${KARANTINACI_SKILL_FALLBACK.find(skill => skill.key === reward.skillKey)?.name || 'Yetenek'} açıldı!`;
  if (reward.type === 'skill_power') return `${KARANTINACI_SKILL_FALLBACK.find(skill => skill.key === reward.skillKey)?.name || 'Yetenek'} güçlendi`;
  return 'Yeni ödül';
}

function BoxOpeningModal({ box, onCollect }) {
  const requiredTaps = Math.max(3, Number(box.requiredTaps) || 3);
  const [taps, setTaps] = useState(0);
  const opened = taps >= requiredTaps;
  const tap = () => {
    if (opened) return;
    const next = Math.min(requiredTaps, taps + 1);
    setTaps(next);
    if (navigator.vibrate) navigator.vibrate(next === requiredTaps ? [35, 35, 70] : 20);
  };
  const rewardCards = (box.rewards || []).map((reward, index) => {
    const RewardIcon = reward.type === 'account_xp' || reward.type === 'xp'
      ? Star
      : reward.type === 'coins'
        ? Coins
        : reward.type.includes('character')
          ? UserRound
          : Zap;
    return <article key={`${reward.type}-${index}`}><RewardIcon aria-hidden="true" /><b>{rewardText(reward)}</b></article>;
  });

  return <div className="modal-backdrop box-opening-backdrop">
    <section className={`box-opening-modal ${box.type} ${opened ? 'opened' : ''}`}>
      <Sparkles className="box-spark left" />
      <Sparkles className="box-spark right" />
      {!opened ? <>
        <span className="eyebrow">{box.name}</span>
        <h2>{requiredTaps - taps > 0 ? `${requiredTaps - taps} kez daha dokun` : 'Açılıyor!'}</h2>
        <button className={`tap-box crack-${taps}`} onPointerDown={event => { event.preventDefault(); tap(); }}>
          <Gift /><i /><b>DOKUN</b>
        </button>
        <div className="tap-meter">{Array.from({ length: requiredTaps }, (_, index) => <i key={index} className={index < taps ? 'on' : ''} />)}</div>
      </> : <>
        <span className="eyebrow">KUTU AÇILDI</span>
        <h2>Ödüllerin hazır!</h2>
        <div className="box-reward-cards">{rewardCards}</div>
        <button className="reveal-accept" onClick={onCollect}>Ödülleri Al</button>
      </>}
    </section>
  </div>;
}

function SettingsView() {
  const [music, setMusic] = useState(() => window.localStorage.getItem('dlink_music') !== 'off');
  const [effects, setEffects] = useState(() => window.localStorage.getItem('dlink_effects') !== 'off');
  const toggle = (key, value, setter) => { const next = !value; setter(next); window.localStorage.setItem(key, next ? 'on' : 'off'); };
  return <div className="page simple-page"><span className="eyebrow">TERCİHLER</span><h1>Ayarlar</h1><p>Oyun merkezini cihazına göre düzenle.</p><section className="settings-list"><Setting icon={Music} title="Müzik" text="Menü ve oyun müzikleri" value={music} onClick={() => toggle('dlink_music', music, setMusic)} /><Setting icon={Zap} title="Ses efektleri" text="Yetenek, anahtar ve sonuç sesleri" value={effects} onClick={() => toggle('dlink_effects', effects, setEffects)} /><Setting icon={Bell} title="Davet bildirimleri" text="Arkadaşlarının oda davetleri" value onClick={() => {}} /></section></div>;
}

function Setting({ icon: Icon, title, text, value, onClick }) {
  return <button className="setting-row" onClick={onClick}><Icon /><span><b>{title}</b><small>{text}</small></span><i className={value ? 'on' : ''}><em /></i></button>;
}

function HelpView() {
  return <div className="page simple-page"><span className="eyebrow">DESTEK</span><h1>Yardım merkezi</h1><p>Oyun kuralları, bağlantı ve hesap konularında hızlı yanıtlar.</p><div className="help-grid"><article><Gamepad2 /><h3>Nasıl oynanır?</h3><p>Her oyun için lobi içindeki görev özetini takip et.</p></article><article><Radio /><h3>Bağlantı sorunu</h3><p>Odan korunur; bağlantın geldiğinde aynı oyuncuyla devam edersin.</p></article><article><ShieldCheck /><h3>Güvenli oyun</h3><p>Tek aktif oda kuralı hesabını korur.</p></article></div></div>;
}

function InviteBanner({ invite, onAccept, onClose }) {
  return <aside className="invite-banner"><button onClick={onClose}><X /></button><ProfileFace avatarId={invite.fromAvatarId} /><div><small>OYUN DAVETİ</small><b>{invite.fromUsername} seni çağırıyor</b><p>{invite.roomName || 'Oyun odası'}</p></div><button className="accept-invite" onClick={onAccept}>Katıl</button></aside>;
}

function Splash() {
  return <div className="splash"><Brand /><span>Dlink Games hazırlanıyor</span></div>;
}

function Landing({ onAuth, children }) {
  return <div className="landing"><header><Brand /><div><button onClick={() => onAuth('login')}>Giriş Yap</button><button className="landing-primary" onClick={() => onAuth('register')}>Ücretsiz Katıl</button></div></header><main><section><span className="eyebrow">ARKADAŞLARIN BURADA</span><h1>Oyun zamanı<br /><b>şimdi başlıyor.</b></h1><p>Odanı kur, arkadaşını çağır, botlarını seç ve aynı maçta buluş.</p><button className="landing-cta" onClick={() => onAuth('register')}>Hemen Başla <ChevronRight /></button></section><div className="landing-cards"><GameShowcaseCard type="mole" title={<>Köstebek<br />Kim?</>} onQuick={() => onAuth('login')} onCreate={() => onAuth('login')} onFriends={() => onAuth('login')} /><GameShowcaseCard type="quarantine" title="Karantinacı" onQuick={() => onAuth('login')} onCreate={() => onAuth('login')} onFriends={() => onAuth('login')} /><GameShowcaseCard type="signal" title="Son Sinyal" onQuick={() => onAuth('login')} onCreate={() => onAuth('login')} onFriends={() => onAuth('login')} /></div></main>{children}</div>;
}

function AuthModal({ mode, error, onMode, onClose, onSubmit }) {
  const register = mode === 'register';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [avatarId, setAvatarId] = useState(1);
  return <div className="modal-backdrop"><section className={`auth-modal ${register ? 'register-modal' : ''}`}><button className="modal-close" onClick={onClose}><X /></button><Brand compact /><h2>{register ? 'Ekibe katıl' : 'Tekrar hoş geldin'}</h2><p>{register ? 'Bir karakter seç. Bu profilin bütün oyunlarda seni temsil edecek.' : 'Oyunlarına kaldığın yerden devam et.'}</p><form onSubmit={event => { event.preventDefault(); onSubmit({ username: username.trim(), password, avatarId }); }}>
    {register && <div className="account-character-picker">{PROFILE_AVATARS.slice(0, 4).map(avatar => <button type="button" key={avatar.id} className={avatarId === avatar.id ? 'selected' : ''} onClick={() => setAvatarId(avatar.id)}><ProfileFace avatarId={avatar.id} className="account-avatar" /><b>{avatar.label}</b>{avatarId === avatar.id && <Check />}</button>)}</div>}
    <label>Kullanıcı adı<input autoFocus={!register} autoComplete="username" value={username} onChange={event => setUsername(event.target.value)} minLength={3} maxLength={12} required /></label>
    <label>Şifre<input type="password" autoComplete={register ? 'new-password' : 'current-password'} value={password} onChange={event => setPassword(event.target.value)} minLength={4} required /></label>
    {error && <div className="form-error">{error}</div>}<button className="auth-submit" type="submit">{register ? 'Hesabımı Oluştur' : 'Giriş Yap'}</button>
  </form><button className="auth-mode" onClick={() => onMode(register ? 'login' : 'register')}>{register ? 'Zaten hesabım var' : 'Yeni bir hesap oluştur'}</button></section></div>;
}

export default App;
